/**
 * AI Ads Studio — Phase 1 MVP (Social Ad mode)
 * Route: /ai-ads-studio
 *
 * Storyboard-first pipeline:
 *   1. Create project (brand kit, aspect ratio).
 *   2. Add product reference image(s) to the Show Kit (immutable).
 *   3. Add 1–4 scenes with a 1-line description + duration.
 *   4. Generate storyboard still per scene (anchored to product refs) — 1 cr.
 *   5. Approve each still (or request one revise — 1 cr).
 *   6. Render motion per scene (Seedance 2.5 preferred, with transparent fallback).
 *   7. Export the full ad as a single MP4 via kaharian_render_mp4.
 */
import { useEffect, useMemo, useState } from 'react';
import { Clapperboard, Plus, Package, Sparkles, Film, Trash2, Download, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

import {
  AI_STUDIO_CFG,
  listProjects,
  createProject,
  getProject,
  deleteProject,
  addKitAsset,
  removeKitAsset,
  addScene,
  removeScene,
  generateStill,
  approveStill,
  renderScene,
  exportProject,
  creditPreview,
} from '../lib/aiStudioApi';
import { AssetChip, CreditPill, ApproveBadge, GenerateStillButton } from '../components/AIStudioShared';

const TONE_OPTIONS = ['energetic', 'cinematic', 'warm', 'playful', 'premium'];

export default function AIAdsStudioPage() {
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newTone, setNewTone] = useState('cinematic');
  const [newAspect, setNewAspect] = useState('9:16');
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [kitForm, setKitForm] = useState({ name: '', urls: '' });
  const [sceneForm, setSceneForm] = useState({ description: '', duration_seconds: 6 });
  const [stillPrompt, setStillPrompt] = useState({});
  const [renderBusy, setRenderBusy] = useState({});
  const [stillBusy, setStillBusy] = useState({});
  const [previewCost, setPreviewCost] = useState({});
  const [resolution, setResolution] = useState('1080p');

  async function refresh() {
    try {
      const list = await listProjects('ads');
      setProjects(list);
      if (!activeId && list.length) setActiveId(list[0].id);
    } catch (e) {
      toast.error(`Could not load projects: ${e.message}`);
    }
  }

  async function loadActive(id) {
    try {
      const p = await getProject(id);
      setActive(p);
    } catch (e) {
      toast.error(`Could not open project: ${e.response?.data?.detail || e.message}`);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (activeId) loadActive(activeId); }, [activeId]);

  // Preview motion credits for the chosen resolution + each scene's duration.
  useEffect(() => {
    const durations = new Set((active?.scenes || []).map((s) => s.duration_seconds));
    durations.forEach(async (d) => {
      const key = `${d}:${resolution}`;
      if (previewCost[key] !== undefined) return;
      try {
        const r = await creditPreview(d, resolution);
        setPreviewCost((p) => ({ ...p, [key]: r.credits }));
      } catch { /* non-fatal */ }
    });
    // eslint-disable-next-line
  }, [active?.scenes, resolution]);

  async function handleCreate() {
    if (!newTitle.trim()) { toast.error('Give the ad a title'); return; }
    setLoading(true);
    try {
      const p = await createProject({
        kind: 'ads',
        title: newTitle.trim(),
        tone: newTone,
        aspect_ratio: newAspect,
        brand_kit: { logo_url: newLogoUrl.trim() },
      });
      setShowCreate(false);
      setNewTitle(''); setNewLogoUrl('');
      await refresh();
      setActiveId(p.id);
      toast.success('Project created');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }

  async function handleAddProduct() {
    if (!active || !kitForm.name.trim()) { toast.error('Name the product reference'); return; }
    const urls = kitForm.urls.split(/[\n,]+/).map((u) => u.trim()).filter(Boolean);
    try {
      await addKitAsset(active.id, {
        kind: 'product',
        name: kitForm.name.trim(),
        reference_urls: urls,
      });
      setKitForm({ name: '', urls: '' });
      await loadActive(active.id);
      toast.success('Product reference locked');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  async function handleAddScene() {
    if (!active) return;
    const desc = sceneForm.description.trim();
    if (!desc) { toast.error('Describe the shot'); return; }
    try {
      await addScene(active.id, {
        description: desc,
        duration_seconds: Number(sceneForm.duration_seconds) || 6,
        anchor_asset_ids: (active.show_kit || []).map((a) => a.id),
      });
      setSceneForm({ description: '', duration_seconds: 6 });
      await loadActive(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  async function handleStill(scene, revision) {
    const prompt = (stillPrompt[scene.id] || scene.still_prompt || scene.description || '').trim();
    if (!prompt) { toast.error('Write a prompt for this still'); return; }
    setStillBusy((b) => ({ ...b, [scene.id]: true }));
    try {
      await generateStill(active.id, scene.id, {
        prompt,
        aspect_ratio: active.aspect_ratio,
        anchor_asset_ids: (active.show_kit || []).map((a) => a.id),
      });
      await loadActive(active.id);
      toast.success(revision ? 'Still revised' : 'Still generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setStillBusy((b) => ({ ...b, [scene.id]: false })); }
  }

  async function handleApprove(scene) {
    try {
      await approveStill(active.id, scene.id);
      await loadActive(active.id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  async function handleRender(scene) {
    const key = `${scene.duration_seconds}:${resolution}`;
    const credits = previewCost[key] ?? '?';
    if (!window.confirm(`Render this scene at ${resolution} for ${credits} credits?`)) return;
    setRenderBusy((b) => ({ ...b, [scene.id]: true }));
    try {
      const r = await renderScene(active.id, scene.id, { resolution });
      await loadActive(active.id);
      toast.success(r.video_url ? 'Scene rendered' : `Scene queued (${r.provider || 'auto'})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setRenderBusy((b) => ({ ...b, [scene.id]: false })); }
  }

  async function handleExport() {
    if (!active) return;
    try {
      const r = await exportProject(active.id);
      await loadActive(active.id);
      toast.success(r.export_url ? 'Export ready' : 'Export queued on the Hub');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  async function handleDeleteProject() {
    if (!active || !window.confirm(`Delete "${active.title}"? This cannot be undone.`)) return;
    try {
      await deleteProject(active.id);
      setActiveId(null); setActive(null);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  const scenes = active?.scenes || [];
  const products = (active?.show_kit || []).filter((a) => a.kind === 'product');
  const anyRendered = useMemo(() => scenes.some((s) => s.video_url), [scenes]);

  return (
    <div className="studio-dark min-h-screen bg-neutral-950 text-neutral-100" data-testid="ai-ads-studio-page">
      <div className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-6">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300">
              <Clapperboard size={14} /> Konnect Media Core · Seedance 2.5
            </div>
            <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">AI Ads Studio</h1>
            <p className="mt-2 max-w-2xl text-sm text-neutral-400">
              Storyboard-first ad generation. Lock your brand kit and product references, approve each still,
              then render reference-anchored motion with native synchronized audio. Credits never burn silently.
            </p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-amber-500 text-neutral-900 hover:bg-amber-400"
            data-testid="ai-ads-create-btn"
          >
            <Plus size={16} className="mr-1" /> New ad project
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          {/* Left rail — project list */}
          <aside className="space-y-2" data-testid="ai-ads-project-list">
            {projects.length === 0 && (
              <Card className="border-neutral-800 bg-neutral-900/40">
                <CardContent className="p-4 text-sm text-neutral-400">
                  No ad projects yet. Hit <strong>New ad project</strong> to start.
                </CardContent>
              </Card>
            )}
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => setActiveId(p.id)}
                data-testid={`ai-ads-project-${p.id}`}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                  activeId === p.id
                    ? 'border-amber-400/60 bg-amber-500/10'
                    : 'border-neutral-800 bg-neutral-900/40 hover:border-neutral-600'
                }`}
              >
                <div className="font-medium">{p.title}</div>
                <div className="text-xs text-neutral-500">{p.aspect_ratio} · {p.tone || '—'}</div>
              </button>
            ))}
          </aside>

          {/* Workspace */}
          {!active ? (
            <Card className="border-neutral-800 bg-neutral-900/40">
              <CardContent className="p-10 text-center text-sm text-neutral-500">
                Select an ad project from the left — or create a new one.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <Card className="border-neutral-800 bg-neutral-900/40">
                <CardHeader className="flex-row items-start justify-between space-y-0">
                  <div>
                    <CardTitle className="text-lg">{active.title}</CardTitle>
                    <div className="mt-1 text-xs text-neutral-500">
                      {active.aspect_ratio} · {active.tone || 'neutral'} ·
                      {' '}{products.length} product{products.length !== 1 ? 's' : ''} locked ·
                      {' '}{scenes.length} scene{scenes.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs text-neutral-200"
                      data-testid="ai-ads-resolution-select"
                    >
                      {AI_STUDIO_CFG.resolutions.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <Button variant="outline" size="sm" onClick={handleDeleteProject}
                      className="border-neutral-700 text-neutral-400 hover:text-red-300" data-testid="ai-ads-delete-project">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              {/* Product references */}
              <Card className="border-neutral-800 bg-neutral-900/40">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Package size={16} /> Product references
                    <span className="text-xs font-normal text-neutral-500">(immutable, up to 3)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {products.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {products.map((p) => (
                        <div key={p.id} className="flex items-center gap-2">
                          <AssetChip asset={p} selected data-testid={`ai-ads-product-${p.id}`} />
                          <button
                            type="button"
                            onClick={() => removeKitAsset(active.id, p.id).then(() => loadActive(active.id))}
                            className="text-xs text-neutral-500 hover:text-red-300"
                            data-testid={`ai-ads-product-remove-${p.id}`}
                          >
                            remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-500">No product references yet. Add one below.</p>
                  )}
                  {products.length < 3 && (
                    <div className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
                      <Input
                        placeholder="Name (e.g. SuperCola Can)"
                        value={kitForm.name}
                        onChange={(e) => setKitForm({ ...kitForm, name: e.target.value })}
                        className="border-neutral-700 bg-neutral-950 text-sm"
                        data-testid="ai-ads-kit-name"
                      />
                      <Input
                        placeholder="Image URL(s) — comma-separated"
                        value={kitForm.urls}
                        onChange={(e) => setKitForm({ ...kitForm, urls: e.target.value })}
                        className="border-neutral-700 bg-neutral-950 text-sm"
                        data-testid="ai-ads-kit-urls"
                      />
                      <Button onClick={handleAddProduct} className="bg-amber-500 text-neutral-900 hover:bg-amber-400"
                        data-testid="ai-ads-kit-add">
                        Lock reference
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Scenes */}
              <Card className="border-neutral-800 bg-neutral-900/40">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2"><Film size={16} /> Shots</span>
                    <Badge className="bg-neutral-800 text-neutral-300">Draft a storyboard — 1 cr / still</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {scenes.length === 0 && (
                    <p className="text-sm text-neutral-500">
                      Describe your first shot. Example: <em>close-up of SuperCola can with condensation, slow dolly in</em>.
                    </p>
                  )}
                  {scenes.map((s, i) => (
                    <SceneCard
                      key={s.id}
                      scene={s}
                      index={i}
                      promptDraft={stillPrompt[s.id] ?? ''}
                      setPromptDraft={(v) => setStillPrompt((p) => ({ ...p, [s.id]: v }))}
                      stillLoading={!!stillBusy[s.id]}
                      renderLoading={!!renderBusy[s.id]}
                      previewCredits={previewCost[`${s.duration_seconds}:${resolution}`]}
                      onStill={(rev) => handleStill(s, rev)}
                      onApprove={() => handleApprove(s)}
                      onRender={() => handleRender(s)}
                      onRemove={() => removeScene(active.id, s.id).then(() => loadActive(active.id))}
                    />
                  ))}
                  {scenes.length < 4 && (
                    <div className="grid gap-2 sm:grid-cols-[1fr_120px_auto]">
                      <Input
                        placeholder="One-line shot description"
                        value={sceneForm.description}
                        onChange={(e) => setSceneForm({ ...sceneForm, description: e.target.value })}
                        className="border-neutral-700 bg-neutral-950 text-sm"
                        data-testid="ai-ads-scene-desc"
                      />
                      <select
                        value={sceneForm.duration_seconds}
                        onChange={(e) => setSceneForm({ ...sceneForm, duration_seconds: Number(e.target.value) })}
                        className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1 text-sm text-neutral-200"
                        data-testid="ai-ads-scene-duration"
                      >
                        {[4, 5, 6, 8, 10, 15].map((d) => <option key={d} value={d}>{d}s</option>)}
                      </select>
                      <Button onClick={handleAddScene}
                        className="bg-neutral-800 text-neutral-100 hover:bg-neutral-700" data-testid="ai-ads-scene-add">
                        <Plus size={14} className="mr-1" /> Add shot
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {anyRendered && (
                <Card className="border-amber-400/30 bg-amber-500/5">
                  <CardContent className="flex items-center justify-between gap-4 p-4">
                    <div>
                      <div className="text-sm font-medium text-amber-200">Export ready</div>
                      <div className="text-xs text-neutral-400">
                        Stitch every rendered shot into one MP4 via the Konnect render pipeline.
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {active.export_url && (
                        <a href={active.export_url} target="_blank" rel="noreferrer"
                          className="text-xs text-amber-300 underline">Download latest</a>
                      )}
                      <Button onClick={handleExport} className="bg-amber-500 text-neutral-900 hover:bg-amber-400"
                        data-testid="ai-ads-export-btn">
                        <Download size={14} className="mr-1" /> Export MP4
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="studio-dark border-neutral-800 bg-neutral-950 text-neutral-100">
          <DialogHeader><DialogTitle>New ad project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title (e.g. SuperCola Summer)" value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="border-neutral-700 bg-neutral-900" data-testid="ai-ads-new-title" />
            <select value={newTone} onChange={(e) => setNewTone(e.target.value)}
              className="w-full rounded-md border border-neutral-700 bg-neutral-900 px-2 py-2 text-sm"
              data-testid="ai-ads-new-tone">
              {TONE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={newAspect} onChange={(e) => setNewAspect(e.target.value)}
              className="w-full rounded-md border border-neutral-700 bg-neutral-900 px-2 py-2 text-sm"
              data-testid="ai-ads-new-aspect">
              {AI_STUDIO_CFG.adAspects.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <Input placeholder="Brand logo URL (optional)" value={newLogoUrl}
              onChange={(e) => setNewLogoUrl(e.target.value)}
              className="border-neutral-700 bg-neutral-900" data-testid="ai-ads-new-logo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={loading} className="bg-amber-500 text-neutral-900 hover:bg-amber-400"
              data-testid="ai-ads-new-submit">
              {loading ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SceneCard({
  scene, index, promptDraft, setPromptDraft, stillLoading, renderLoading,
  previewCredits, onStill, onApprove, onRender, onRemove,
}) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-950/60 p-4" data-testid={`ai-ads-scene-${scene.id}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className="bg-neutral-800 text-neutral-200">Shot {index + 1}</Badge>
          <span className="text-xs text-neutral-500">{scene.duration_seconds}s</span>
          <ApproveBadge approved={scene.still_approved} />
        </div>
        <button onClick={onRemove} className="text-xs text-neutral-500 hover:text-red-300"
          data-testid={`ai-ads-scene-remove-${scene.id}`}>remove</button>
      </div>
      <p className="mb-3 text-sm text-neutral-300">{scene.description}</p>

      <div className="grid gap-3 md:grid-cols-[200px_1fr]">
        <div className="flex items-center justify-center rounded-md border border-neutral-800 bg-neutral-950 p-2">
          {scene.still_url ? (
            <img alt="Storyboard still" src={scene.still_url} className="h-32 w-full rounded object-cover" />
          ) : (
            <div className="flex h-32 w-full flex-col items-center justify-center gap-1 text-neutral-500">
              <ImageIcon size={18} />
              <span className="text-xs">No still yet</span>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Textarea
            rows={2}
            placeholder="Prompt for this still — the system auto-attaches your product references."
            value={promptDraft || scene.still_prompt || ''}
            onChange={(e) => setPromptDraft(e.target.value)}
            className="border-neutral-700 bg-neutral-900 text-sm"
            data-testid={`ai-ads-still-prompt-${scene.id}`}
          />
          <div className="flex flex-wrap items-center gap-2">
            <GenerateStillButton
              onClick={() => onStill(!!scene.still_url)}
              loading={stillLoading}
              revision={!!scene.still_url}
              data-testid={`ai-ads-still-btn-${scene.id}`}
            />
            {scene.still_url && !scene.still_approved && (
              <Button size="sm" variant="outline" onClick={onApprove}
                className="border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10"
                data-testid={`ai-ads-approve-btn-${scene.id}`}>
                Approve still
              </Button>
            )}
            <Button
              size="sm"
              onClick={onRender}
              disabled={!scene.still_approved || renderLoading}
              className="bg-neutral-100 text-neutral-900 hover:bg-white disabled:opacity-40"
              data-testid={`ai-ads-render-btn-${scene.id}`}
            >
              <Sparkles size={14} className="mr-1" />
              {renderLoading
                ? 'Rendering…'
                : scene.video_url
                  ? `Re-render — ${previewCredits ?? '…'} cr`
                  : `Render motion — ${previewCredits ?? '…'} cr`}
            </Button>
            <CreditPill cost={previewCredits ?? '…'} label="motion" data-testid={`ai-ads-cost-${scene.id}`} />
          </div>
          {scene.video_url && (
            <div className="pt-2">
              <video src={scene.video_url} controls className="h-40 w-full rounded border border-neutral-800" />
              <div className="mt-1 text-[11px] text-neutral-500">
                Provider: {scene.video_provider || 'auto'} · Resolution: {scene.resolution || '—'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
